import { addMonths, format, getDate, getDaysInMonth, isBefore, isLeapYear, isValid, parse, setDate, setMonth, setYear, startOfDay, startOfMonth } from 'date-fns';
import { ChevronLeft, ChevronRight } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import DatePicker from "react-datepicker";
import { usePopper } from 'react-popper';
import { MaximumStay, MinimumStay } from '../../../api/listings-client';
import { createPortal } from 'react-dom';

interface CustomDatePickerProps {
    availableFrom: string | null; // String formatted yyyy-MM-dd
    setAvailableFrom: (availableFrom: string) => void;
    disablePastDates?: boolean;
}

const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
    availableFrom,
    setAvailableFrom,
    disablePastDates = true
}) => {
    const [currentMonth, setCurrentMonth] = useState(startOfMonth(new Date()));
    const [inputValue, setInputValue] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [calendarDate, setCalendarDate] = useState(new Date());
    const calendarRef = useRef(null);
    const inputRef = useRef(null);
    const [hasInteracted, setHasInteracted] = useState(false);
    const [popperElement, setPopperElement] = useState(null);
    
    const { styles, attributes, update } = usePopper(inputRef.current, popperElement, {
        placement: 'bottom',
        strategy: 'fixed', // Use fixed positioning
        modifiers: [
            { name: 'offset', options: { offset: [0, 10] } },
            { name: 'preventOverflow', options: { boundary: 'viewport' } },
        ],
    });

    useEffect(() => {
        if (availableFrom) {
            setInputValue(format(availableFrom, 'dd/MM/yyyy'));
            setCurrentMonth(startOfMonth(availableFrom));
            setCalendarDate(parse(availableFrom, 'yyyy-MM-dd', new Date()));
        } else {
            setInputValue('');
            setCurrentMonth(startOfMonth(new Date()));
            setCalendarDate(new Date());
        }
    }, [availableFrom]);
    useEffect(() => {
        if (isOpen && update) {
            update();

            const handleScroll = () => {
                requestAnimationFrame(() => {
                    if (update) update();
                });
            };

            window.addEventListener('scroll', handleScroll, true);

            return () => {
                window.removeEventListener('scroll', handleScroll, true);
            };
        }
    }, [isOpen, update]);

    const isDateValid = (date) => {
        return date instanceof Date && isValid(date) && (!disablePastDates || !isBefore(date, startOfDay(new Date())));
    };

    const parsePartialDate = (value) => {
        const now = new Date();
        const [dayStr, monthStr, yearStr] = value.split('/');

        let parsedDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        const parsePartialNumber = (str, max) => {
            if (!str) return null;
            const num = parseInt(str, 10);
            return (isNaN(num) || num > max) ? null : num;
        };

        // There may be some logic in here that doesn't work or some edge cases missed, but it's a good enough effort considering
        // you can just click the date you want.
        if (monthStr) {
            const targetMonth = parsePartialNumber(monthStr, 12);

            if (targetMonth !== null && targetMonth > 0) {
                let targetDay = parsePartialNumber(dayStr, 31);
                let newDate = setMonth(parsedDate, targetMonth - 1);

                // Handle the leap year edge case
                if (targetDay === 29 && targetMonth === 2) {
                    let tempDate = setDate(parsedDate, targetDay);
                    tempDate = setMonth(parsedDate, targetMonth - 1);
                    if (isLeapYear(parsedDate) && isBefore(now, tempDate)) {
                        // Happy scenario. We're before 29/2 in a leap year
                        newDate = tempDate;
                    } else {
                        // Set it to the first future leap year
                        newDate = setYear(parsedDate, parsedDate.getFullYear() + 1);
                        while (!isLeapYear(newDate)) {
                            newDate = setYear(newDate, newDate.getFullYear() + 1);
                        }
                        newDate = setDate(newDate, targetDay);
                        newDate = setMonth(newDate, targetMonth - 1);
                    }
                } else {
                    // We trust that the user cares more about the month they entered than the day
                    // So we just set the day to the max in the month if it exceeds
                    const daysInNewMonth = getDaysInMonth(newDate);
                    if (targetDay !== null) {
                        if (targetDay > daysInNewMonth) {
                            newDate = setDate(newDate, daysInNewMonth);
                        } else {
                            newDate = setDate(newDate, targetDay);
                        }
                    } else {
                        newDate = setDate(newDate, daysInNewMonth);
                    }

                    if (isBefore(newDate, now)) {
                        newDate = setYear(newDate, newDate.getFullYear() + 1);
                    }
                }

                parsedDate = newDate;
            }
        } else if (dayStr) {
            let targetDay;
            // User has only typed 1 digit in total
            if (dayStr.length === 1) {
                targetDay = parsePartialNumber(dayStr, 9);
                if (targetDay !== null) {
                    let curDate = getDate(parsedDate);

                    if (targetDay < curDate && targetDay <= 3) {
                        if (targetDay * 10 >= curDate) {
                            targetDay = targetDay * 10;
                        } else if (curDate - targetDay * 10 <= 9) {
                            targetDay = curDate;
                        } else {
                            // Up the month
                            parsedDate = addMonths(parsedDate, 1);
                        }
                    }

                    const daysInMonth = getDaysInMonth(parsedDate);
                    if (targetDay > daysInMonth) {
                        // No suitable day in the month is valid that starts with this digit.
                        // The user typed in a single digit so there must exist a day in the next month
                        parsedDate = addMonths(parsedDate, 1);
                    } else {
                        parsedDate = setDate(parsedDate, targetDay);
                    }
                }
            } else {
                // 2 or more digits
                targetDay = parsePartialNumber(dayStr, 31);

                if (targetDay !== null) {
                    // Check if the target day is valid for the current month
                    let daysInMonth = getDaysInMonth(parsedDate);
                    if (targetDay > daysInMonth) {
                        // Move to the next month where the day is valid
                        while (targetDay > daysInMonth) {
                            parsedDate = addMonths(parsedDate, 1);
                            daysInMonth = getDaysInMonth(parsedDate);
                        }
                    }
                    parsedDate = setDate(parsedDate, targetDay);
                    if (isBefore(parsedDate, now)) {
                        parsedDate = addMonths(parsedDate, 1);
                        while (targetDay > daysInMonth) {
                            parsedDate = addMonths(parsedDate, 1);
                            daysInMonth = getDaysInMonth(parsedDate);
                        }
                    }
                }
            }
        }

        // Year parsing
        if (yearStr) {
            const currentYear = now.getFullYear();
            let targetYear;

            if (yearStr.length === 4) {
                targetYear = parsePartialNumber(yearStr, 9999);
            } else {
                targetYear = parsePartialNumber(yearStr + currentYear.toString().slice(yearStr.length, 4), 9999);
                if (targetYear && targetYear < currentYear) {
                    targetYear += Math.pow(10, 4 - yearStr.length);
                }
            }

            if (targetYear !== null) {
                parsedDate = setYear(parsedDate, targetYear);

                if (isBefore(parsedDate, startOfDay(now))) {
                    parsedDate = setYear(parsedDate, targetYear + 1);
                }

                // Cheap way to handle the 29/2 edge case if it's not the current year
                if (dayStr && monthStr) {
                    const targetDay = parsePartialNumber(dayStr, 31);
                    const targetMonth = parsePartialNumber(monthStr, 12);

                    if (targetDay && targetMonth && targetDay === 29 && targetMonth === 2) {
                        parsedDate = setDate(parsedDate, targetDay);
                        parsedDate = setMonth(parsedDate, targetMonth - 1);
                    }
                }
            }
        }

        // Ensure the date is not in the past if disablePastDates is true
        if (disablePastDates && isBefore(parsedDate, startOfDay(now))) {
            parsedDate = startOfDay(now);
        }

        console.log(parsedDate);

        return parsedDate;
    };

    const handleInputChange = (e) => {
        const value = e.target.value;
        setInputValue(value);
        setHasInteracted(true);

        if (value.length > 0) {
            let newDate = parsePartialDate(value);
            console.log("let newDate = parsePartialDate(value); setting calendar date " + newDate);
            setCalendarDate(newDate);
            setCurrentMonth(startOfMonth(newDate));
        }
    };

    const handleInputBlur = () => {
        setIsOpen(false);
        if (!hasInteracted) {
            // Reset the current month otherwise it won't open to the month view for the current date.
            if (calendarDate) setCurrentMonth(startOfMonth(calendarDate));
            return;
        }
    
    
        if (calendarDate && isDateValid(calendarDate)) {
            setAvailableFrom(format(calendarDate, 'yyyy-MM-dd'));
            setInputValue(format(calendarDate, 'dd/MM/yyyy'));
        } else {
            const parsedDate = parsePartialDate(inputValue);
            if (isDateValid(parsedDate)) {
                setAvailableFrom(format(parsedDate, 'yyyy-MM-dd'));
                console.log("setAvailableFrom(format(parsedDate, 'yyyy-MM-dd')) setting calendar date " + parsedDate);
                setCalendarDate(parsedDate);
                setInputValue(format(parsedDate, 'dd/MM/yyyy'));
            } else if (calendarDate) {
                setInputValue(format(calendarDate, 'dd/MM/yyyy'));
                console.log("setInputValue(format(selectedDate, 'dd/MM/yyyy')); setting calendar date " + calendarDate);
            } else {
                const newDate = new Date();
                setAvailableFrom(format(newDate, 'yyyy-MM-dd'));
                console.log("setAvailableFrom(format(newDate, 'yyyy-MM-dd')); setting calendar date " + newDate);
                setCalendarDate(newDate);
                setInputValue(format(newDate, 'dd/MM/yyyy'));
            }
        }
        
        setHasInteracted(false);
    };

    const handleOutsideClick = useCallback((event) => {
        if (isOpen && calendarRef.current && !calendarRef.current.contains(event.target) &&
            inputRef.current && !inputRef.current.contains(event.target)) {
            handleInputBlur();
        }
    }, [isOpen]);

    useEffect(() => {
        document.addEventListener('mousedown', handleOutsideClick);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, [handleOutsideClick]);

    useEffect(() => {
        document.addEventListener('mousedown', handleOutsideClick);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, []);

    const CustomHeader = ({
        date,
        decreaseMonth,
        increaseMonth,
        prevMonthButtonDisabled,
        nextMonthButtonDisabled,
    }) => (
        <div className="flex items-center justify-between px-2 py-2">
            <button
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    decreaseMonth();
                }}
                disabled={prevMonthButtonDisabled}
                type="button"
                className="p-1 text-gray-800 hover:bg-gray-100 rounded-full disabled:opacity-50 disabled:pointer-events-none"
            >
                <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-lg text-gray-800 font-medium">
                {date.toLocaleString('default', { month: 'long' })} {date.getFullYear()}
            </span>
            <button
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    increaseMonth();
                }}
                disabled={nextMonthButtonDisabled}
                type="button"
                className="p-1 text-gray-800 hover:bg-gray-100 rounded-full disabled:opacity-50 disabled:pointer-events-none"
            >
                <ChevronRight className="w-5 h-5" />
            </button>
        </div>
    );

    const CustomDay = ({ dayOfMonth, date }) => {
        const isSelected = calendarDate instanceof Date && date.toDateString() === calendarDate.toDateString();
        const isCurrentMonth = date.getMonth() === currentMonth.getMonth();
        const isPastDate = disablePastDates && isBefore(date, startOfDay(new Date()));

        return (
            <div className="react-datepicker__day-wrapper flex items-center justify-center">
                <button
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (!isPastDate) {
                            setAvailableFrom(format(date, 'yyyy-MM-dd'));
                            console.log("if (!isPastDate) { setting calendar date " + date);
                            setCalendarDate(date);
                            setCurrentMonth(startOfMonth(date));
                            setInputValue(format(date, 'dd/MM/yyyy'));
                            setIsOpen(false);
                            setHasInteracted(true);
                        }
                    }}
                    type="button"
                    className={`
                        w-10 h-10 flex items-center justify-center text-sm rounded-full border border-transparent
                        ${isSelected ? 'bg-blue-600 text-white font-medium' : 'hover:border-blue-600 hover:text-[#2476f2] font-normal'}
                        ${!isCurrentMonth || isPastDate ? 'text-gray-300 pointer-events-none' : ''}
                    `}
                    disabled={!isCurrentMonth || isPastDate}
                >
                    {dayOfMonth}
                </button>
            </div>
        );
    };

    const calendarPopup = isOpen && createPortal(
        <div
            ref={setPopperElement}
            style={{
                ...styles.popper,
                zIndex: 9999,
            }}
            {...attributes.popper}
        >
            <div ref={calendarRef}>
                <DatePicker
                    selected={calendarDate}
                    onChange={(date) => {
                        if (date !== null) {
                            setAvailableFrom(format(date, 'yyyy-MM-dd'));
                            setCalendarDate(date);
                            setInputValue(format(date, 'dd/MM/yyyy'));
                            setCurrentMonth(startOfMonth(date));
                            setIsOpen(false);
                            setHasInteracted(true);
                        }
                    }}
                    onMonthChange={(date) => {
                        setCurrentMonth(startOfMonth(date));
                    }}
                    renderCustomHeader={CustomHeader}
                    inline
                    renderDayContents={(dayOfMonth, date) => (
                        <CustomDay dayOfMonth={dayOfMonth} date={date} />
                    )}
                    weekDayClassName={() => "text-gray-500 my-2"}
                    minDate={disablePastDates ? new Date() : undefined}
                    monthsShown={1}
                    showMonthYearPicker={false}
                    openToDate={currentMonth}
                />
            </div>
        </div>,
        document.body
    );

    return (
        <div className="relative">
            <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                onFocus={() => {
                    setIsOpen(true);
                    setHasInteracted(false);
                }}
                onBlur={(e) => {
                    requestAnimationFrame(() => {
                        if (!calendarRef.current?.contains(document.activeElement)) {
                            handleInputBlur();
                        }
                    });
                }}
                placeholder={format(new Date(), 'dd/MM/yyyy')}
                className="py-3 px-4 block w-full border border-gray-200 rounded-lg text-base focus:z-10 focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
            />
            {calendarPopup}
        </div>
    );

};


const CustomDropdown = ({ options, value, onChange, placeholder, visibleItems = 5 }) => {
    const [isOpen, setIsOpen] = useState(false);
    const triggerRef = useRef(null);
    const dropdownRef = useRef(null);
    const [popperElement, setPopperElement] = useState(null);

    const { styles, attributes, update } = usePopper(triggerRef.current, popperElement, {
        placement: 'bottom-start',
        strategy: 'fixed',
        modifiers: [
            { name: 'offset', options: { offset: [0, 4] } },
            { name: 'preventOverflow', options: { padding: 8 } },
        ],
    });

    useEffect(() => {
        if (isOpen && update) {
            update();

            const handleScroll = () => {
                requestAnimationFrame(() => {
                    if (update) update();
                });
            };

            window.addEventListener('scroll', handleScroll, true);

            return () => {
                window.removeEventListener('scroll', handleScroll, true);
            };
        }
    }, [isOpen, update]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
                triggerRef.current && !triggerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleToggle = () => {
        setIsOpen(!isOpen);
    };

    const handleSelect = (option) => {
        onChange(option);
        setIsOpen(false);
    };

    const itemHeight = 40; // Approximate height of each item in pixels

    const dropdownList = isOpen && createPortal(
        <div
            ref={setPopperElement}
            style={{
                ...styles.popper,
                zIndex: 9999,
                width: triggerRef.current ? triggerRef.current.offsetWidth : 'auto',
            }}
            {...attributes.popper}
        >
            <div
                ref={dropdownRef}
                className="p-1 space-y-0.5 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden overflow-y-auto"
                style={{
                    maxHeight: `${itemHeight * visibleItems}px`
                }}
            >
                {options.map((option, index) => (
                    <div
                        key={index}
                        onClick={() => handleSelect(option)}
                        className="cursor-pointer py-2 px-4 w-full text-base text-gray-800 hover:bg-gray-100 rounded-md focus:outline-none focus:bg-gray-100"
                    >
                        <div className="flex justify-between items-center w-full">
                            <span>{option}</span>
                            {value === option && (
                                <span>
                                    <svg className="flex-shrink-0 size-3.5 text-[#24ccf2]" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                </span>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>,
        document.body
    );

    return (
        <div className="relative">
            <button
                ref={triggerRef}
                type="button"
                onClick={handleToggle}
                className="relative py-3 px-4 pe-9 flex text-nowrap w-full cursor-pointer bg-white border border-gray-200 rounded-lg text-start text-base focus:border-blue-500 focus:ring-blue-500 before:absolute before:inset-0 before:z-[1]"
            >
                <span className="truncate">{value || placeholder}</span>
                <span className="absolute top-1/2 end-3 -translate-y-1/2">
                    <svg className="flex-shrink-0 size-3.5 text-gray-500" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m7 15 5 5 5-5" />
                        <path d="m7 9 5-5 5 5" />
                    </svg>
                </span>
            </button>
            {dropdownList}
        </div>
    );
};

interface TimeStepProps {
    availableFrom: string | null;
    setAvailableFrom: (availableFrom: string) => void;
    minimumStay: MinimumStay | null;
    setMinimumStay: React.Dispatch<React.SetStateAction<MinimumStay | null>>;
    maximumStay: MaximumStay | null;
    setMaximumStay: React.Dispatch<React.SetStateAction<MaximumStay | null>>;
    editMode: boolean;
}

const TimeStep: React.FC<TimeStepProps> = ({
    availableFrom,
    setAvailableFrom,
    minimumStay,
    setMinimumStay,
    maximumStay,
    setMaximumStay,
    editMode = false,
}) => {
    const [filteredMinimumStayOptions, setFilteredMinimumStayOptions] = useState(Object.values(MinimumStay));
    const [filteredMaximumStayOptions, setFilteredMaximumStayOptions] = useState(Object.values(MaximumStay));

    // Helper function to compare stay durations
    const compareDurations = (a, b) => {
        const order = ['no minimum', '1 week', '1 month', '2 months', '3 months', '6 months', '9 months', '12 months', '12 months+'];
        return order.indexOf(a) - order.indexOf(b);
    };

    useEffect(() => {
        if (maximumStay) {
            setFilteredMinimumStayOptions(
                Object.values(MinimumStay).filter(stay => compareDurations(stay, maximumStay) <= 0)
            );
        } else {
            setFilteredMinimumStayOptions(Object.values(MinimumStay));
        }
    }, [maximumStay]);

    useEffect(() => {
        if (minimumStay) {
            setFilteredMaximumStayOptions(
                Object.values(MaximumStay).filter(stay => compareDurations(stay, minimumStay) >= 0)
            );
        } else {
            setFilteredMaximumStayOptions(Object.values(MaximumStay));
        }
    }, [minimumStay]);

    const handleMinimumStayChange = (value) => {
        setMinimumStay(value);
        if (maximumStay && compareDurations(value, maximumStay) > 0) {
            setMaximumStay(value);
        }
    };

    const handleMaximumStayChange = (value) => {
        setMaximumStay(value);
        if (minimumStay && compareDurations(value, minimumStay) < 0) {
            setMinimumStay(value);
        }
    };

    return (
        <div className="w-full max-w-3xl mx-auto px-4">
            {!editMode && (
                <h1 className="text-2xl font-bold text-gray-800 sm:text-4xl mb-8 text-center">
                    When would you like to rent it out?
                </h1>
            )}
            
            <div className="space-y-6">
                {/* Available From */}
                <div className="space-y-2 flex flex-col items-center">
                    <div className="text-lg font-medium text-gray-700">
                        Available from
                    </div>
                    <div className="w-48">
                        <CustomDatePicker
                            availableFrom={availableFrom}
                            setAvailableFrom={setAvailableFrom}
                        />
                    </div>
                </div>

                {/* Minimum Stay */}
                <div className="space-y-2 flex flex-col items-center">
                    <div className="text-lg font-medium text-gray-700">
                        Minimum stay
                    </div>
                    <div className="w-48">
                        <CustomDropdown
                            options={filteredMinimumStayOptions}
                            value={minimumStay}
                            onChange={handleMinimumStayChange}
                            placeholder="Select minimum stay"
                            visibleItems={5}
                        />
                    </div>
                </div>

                {/* Maximum Stay */}
                <div className="space-y-2 flex flex-col items-center">
                    <div className="text-lg font-medium text-gray-700">
                        Maximum stay
                    </div>
                    <div className="w-48">
                        <CustomDropdown
                            options={filteredMaximumStayOptions}
                            value={maximumStay}
                            onChange={handleMaximumStayChange}
                            placeholder="Select maximum stay"
                            visibleItems={5}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TimeStep;
